import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Users, Clock, Settings } from 'lucide-react';
import { Button } from './ui/button';

const FlexibleWorkspace = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('office-space');
  const [searchQuery, setSearchQuery] = useState('');

  const workspaceCategories = [
    {
      id: 'office-space',
      label: 'Office Space',
      active: true
    },
    {
      id: 'coworking',
      label: 'Co-working',
      active: false
    },
    
    {
      id: 'virtual-offices',
      label: 'Virtual Offices',
      active: false
    },

    {
      id: 'meeting-rooms',
      label: 'Meeting Room',
      active: false
    }
  ];

  const workspaceOptions = {
    'office-space': [
      {
        id: 'private-cabin',
        title: 'Private Cabin',
        description: 'Premium workspace with complete privacy and professional ambience for focused work and confidential discussions.',
        image: './images-optimized/galery-images/IMG_6808.webp',
        features: ['Premium Workspace', 'Ergonomic Setup', 'Meeting Access', 'Confidential Focus', 'Professional Ambience', 'Elevated Experience', 'Seamless Connectivity', 'Business Privacy'],
        pricing: '₹8,999/seat',
        cta: 'Reserve Now',
        ctaSecondary: 'Learn more'
      },
      {
        id: 'day-offices',
        title: 'Day Offices',
        description: 'Flexible access premium setup with no commitment required. Perfect for professionals who need workspace on-demand.',
        image: './images-optimized/galery-images/IMG_6855.webp',
        features: ['Flexible Access', 'Premium Setup', 'Ergonomic Comfort', 'High-Speed Connectivity', 'Meeting Access', 'Professional Ambience', 'No Commitment', 'Productive Environment'],
        pricing: '₹500/day',
        cta: 'Reserve Now',
        ctaSecondary: 'Learn more'
      },
     
    ],
    'coworking': [
      {
        id: 'single-seat-cabin',
        title: 'Single Seat Private Cabin',
        description: 'Dedicated workspace with complete privacy and ergonomic comfort. Perfect for focused work and confidential discussions.',
        image: './images-optimized/galery-images/IMG_6843.webp',
        features: ['Dedicated Workspace', 'Ergonomic Comfort', 'Complete Privacy', 'Professional Setting', 'Meeting Access', 'Quiet Environment', 'Seamless Productivity', 'Premium Interiors', 'Confidential Discussions', 'Focused Ambience', 'High-Speed Internet'],
        pricing: '₹8,999/month',
        cta: 'Reserve Now',
        ctaSecondary: 'Learn more'
      },
      {
        id: '2-seat-cabin',
        title: '2 Seat Private Cabin',
        description: 'Ideal workspace for partners with premium interiors and seamless collaboration setup for enhanced productivity.',
        image: './images-optimized/galery-images/IMG_6813.webp',
        features: ['Ideal for Partners', 'Premium Workspace', 'Elegant Interiors', 'Confidential Meetings', 'Seamless Collaboration', 'Professional Ambience', 'Enhanced Productivity', 'Business Growth', 'Comfort Redefined', 'High-Speed Internet'],
        pricing: '₹16,999/month',
        cta: 'Reserve Now',
        ctaSecondary: 'Learn more'
      },
      {
        id: '6-seat-cabin',
        title: '6 Seat Private Cabin',
        description: 'Spacious team cabin with elegant interiors and collaborative ambience. Perfect for team synergy and business growth.',
        image: './images-optimized/galery-images/IMG_E6811.webp',
        features: ['Spacious Team Cabin', 'Elegant Interiors', 'Ergonomic Setup', 'Complete Privacy', 'Collaborative Ambience', 'Professional Environment', 'Focus-Driven Space', 'Productive Atmosphere', 'Team Synergy', 'Business Growth'],
        pricing: '₹41,999/month',
        cta: 'Reserve Now',
        ctaSecondary: 'Learn more'
      },
       
    ],
    'virtual-offices': [
      {
        id: 'company-registration',
        title: 'New Company Registration Plan',
        description: 'Register your company at premium addresses in any city in India without taking a physical office space.',
        image: 'https://images.pexels.com/photos/290275/pexels-photo-290275.jpeg?cs=srgb&dl=pexels-pixabay-290275.webp&fm=.webp',
        features: ['Company Registration Address', 'GST Registration', 'Mail & Courier Handling', 'Address on Business Cards, Letter Heads'],
        pricing: 'From ₹1299/Month',
        cta: 'Get started',
        ctaSecondary: 'Learn more'
      },
      {
        id: 'gst-registration',
        title: 'New GST Registration',
        description: 'Expand your existing business operations into new states with ease. Our service helps you register for GST Additional Place of Business (APOB) quickly and compliantly.',
        image: './images-optimized/GST TAX Image.png',
        features: ['GST Registration for Additional Place of Business (APOB)', 'Mail & Courier Handling', 'Address on Business Cards, Letter Heads'],
        pricing: 'From ₹999/Month',
        cta: 'Get started',
        ctaSecondary: 'Learn more'
      },
      {
        id: 'business-address',
        title: 'Business Address Plan',
        description: 'Get a professional business address for your company at prime locations across major cities in India without the cost of renting a physical office.',
        image: './images-optimized/bap.webp',
        features: ['Mail & Courier Handling Services', 'Business Address for Official Use', 'Prime Location Advantage', 'Brand Credibility Enhancement'],
        pricing: 'From ₹416/Month',
        cta: 'Get started',
        ctaSecondary: 'Learn more'
      },
    ],
    'meeting-rooms': [
      {
        id: 'meeting-room',
        title: 'Meeting Room',
        description: 'Modern meeting spaces with smart presentation setup and collaborative ambience for productive discussions. Available in 4, 6, 8 seater options.',
        image: './images-optimized/galery-images/IMG_E6855.webp',
        features: ['Modern Meeting Spaces', 'Elegant Interiors', 'Smart Presentation Setup', 'Collaborative Ambience', 'Confidential Environment', 'Professional Impression', 'Seamless Connectivity', 'Productive Discussions', 'Client-Ready Setup', 'Inspiring Atmosphere'],
        pricing: 'Starts from ₹799/hour',
        cta: 'Book now',
        ctaSecondary: 'View details'
      },
      {
        id: 'interview-room',
        title: 'Interview Room',
        description: 'Professional ambience with confidential environment and comfortable seating. Ideal for HR and client meetings.',
        image: 'https://cdn.uconnectlabs.com/wp-content/uploads/sites/50/2019/11/Interview-Room-e1572972675155.webp',
        features: ['Professional Ambience', 'Confidential Environment', 'Comfortable Seating', 'Noise-Free Space', 'Candidate-Friendly Setup', 'Smart Presentation Ready', 'Impressive First Impression', 'Flexible Booking Options', 'Hassle-Free Access', 'Ideal for HR & Client Meetings'],
        pricing: '₹799/hour',
        cta: 'Book now',
        ctaSecondary: 'View details'
      },
      {
        id: 'training-room',
        title: 'Training Room',
        description: 'Spacious setup with modern presentation tools and collaborative environment. Perfect for workshops and team development.',
        image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQOhAC6Q2P9vJWHmHwZDacpseZ8KB_ECsu9MA&s',
        features: ['Spacious Setup', 'Collaborative Environment', 'Modern Presentation Tools', 'Comfortable Seating', 'Distraction-Free Space', 'Ideal for Workshops & Seminars', 'Professional Ambience', 'Flexible Layout Options', 'Seamless Connectivity', 'Perfect for Team Development'],
        pricing: '₹799/hour',
        cta: 'Book now',
        ctaSecondary: 'View details'
      },
       
    ]
  };

  const getFeatureIcon = (feature) => {
    if (feature.toLowerCase().includes('month') || feature.toLowerCase().includes('year')) {
      return <Calendar size={16} className="text-gray-600" />;
    }
    if (feature.toLowerCase().includes('capacity') || feature.toLowerCase().includes('people')) {
      return <Users size={16} className="text-gray-600" />;
    }
    if (feature.toLowerCase().includes('hour') || feature.toLowerCase().includes('day')) {
      return <Clock size={16} className="text-gray-600" />;
    }
    return <Settings size={16} className="text-gray-600" />;
  };

  const handleCardClick = (option) => {
    if (option.isSpecial) {
      // Handle special "helping hand" card
      navigate('/contact');
    } else {
      // Navigate to specific product or category
      navigate(`/${activeTab}`);
    }
  };

  const currentOptions = workspaceOptions[activeTab] || [];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#e30613]/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#e30613]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#e30613]/3 to-[#c1050f]/3 rounded-full blur-3xl opacity-30"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="relative inline-block">
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Flexible Workspace
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#e30613] to-[#c1050f]">
                Designed Around You
              </span>
            </h2>
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-[#e30613]/20 rounded-full blur-sm"></div>
            <div className="absolute -bottom-2 -right-6 w-6 h-6 bg-[#e30613]/20 rounded-full blur-sm"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Our flexible, fully customizable office rentals are available by the hour, day, or as long as you need. 
            <span className="font-semibold text-gray-800"> Choose what works best for your business.</span>
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#e30613] to-[#c1050f] mx-auto rounded-full"></div>
        </div>

        {/* Category Tabs */}
        <div className="bg-white rounded-2xl p-2 shadow-lg mb-8 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {workspaceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`px-6 py-5 font-bold text-lg rounded-xl transition-all duration-300 transform hover:scale-105 ${
                  activeTab === category.id
                    ? 'bg-gradient-to-r from-[#e30613] to-[#c1050f] text-white shadow-lg shadow-[#e30613]/30'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-[#e30613] hover:shadow-md'
                }`}
              >
                <span className="block text-center leading-tight">
                  {category.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Workspace Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
          {currentOptions.map((option) => (
            <div
              key={option.id}
              className={`bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer group transform hover:scale-105 hover:-translate-y-2 ${
                option.isSpecial ? 'bg-gradient-to-br from-[#e30613] via-[#c1050f] to-[#a00f0a] text-white shadow-xl shadow-[#e30613]/20' : 'hover:shadow-gray-200/50'
              }`}
              onClick={() => handleCardClick(option)}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={option.image}
                  alt={option.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300/46c1be/ffffff?text=Laksh+Space';
                  }}
                />
                {option.isSpecial && (
                  <div className="absolute inset-0 bg-gradient-to-t from-[#e30613]/80 to-transparent" />
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className={`text-lg font-bold mb-3 ${option.isSpecial ? 'text-white' : 'text-gray-900'}`}>
                  {option.title}
                </h3>
                
                <p className={`text-sm mb-4 leading-relaxed ${option.isSpecial ? 'text-white/90' : 'text-gray-600'}`}>
                  {option.description}
                </p>

                {/* Features */}
                {option.features && (
                  <div className="mb-4 space-y-2">
                    {option.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        {getFeatureIcon(feature)}
                        <span className={option.isSpecial ? 'text-white/90' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pricing */}
                {option.pricing && (
                  <div className="mb-6">
                    <p className={`font-semibold ${option.isSpecial ? 'text-white' : 'text-gray-900'}`}>
                      {option.pricing}+GST
                    </p>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <Button 
                    onClick={() => window.location.href = 'https://wa.me/917348824242'}
                    className={`w-full font-bold rounded-md transition-colors ${
                      option.isSpecial 
                        ? 'bg-white text-[#e30613] hover:bg-gray-100' 
                        : 'bg-[#e30613] hover:bg-[#c1050f] text-white'
                    }`}
                  >
                    {option.cta}
                  </Button>
                  
                  {option.ctaSecondary && (
                    <Button 
                      variant="outline"
                      className={`w-full font-bold rounded-md transition-colors ${
                        option.isSpecial 
                          ? 'border-white text-white hover:bg-white hover:text-[#e30613]' 
                          : 'border-[#e30613] text-[#e30613] hover:bg-[#e30613] hover:text-white'
                      }`}
                    >
                      {option.ctaSecondary}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-16">
          <Button 
            onClick={() => navigate('/products')}
            className="bg-gradient-to-r from-[#e30613] to-[#c1050f] hover:from-[#c1050f] hover:to-[#a00f0a] text-white font-black text-lg px-12 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl shadow-[#e30613]/30 hover:shadow-2xl hover:shadow-[#e30613]/40"
          >
            🚀 Explore All Workspace Solutions
          </Button>
          <p className="text-gray-500 mt-4 text-sm">
            Discover the perfect workspace for your needs
          </p>
        </div>
      </div>
    </section>
  );
};

export default FlexibleWorkspace;
